import React, { useEffect, useState, useMemo } from "react";
import { api, fmtEuro, todayISO } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Trash2, Loader2, FileText, Download, ArrowRightCircle } from "lucide-react";
import { toast } from "sonner";
import { generateDocumentPDF } from "@/lib/pdf";

const TYPE_LABEL = { devis: "Devis", facture: "Facture", recu: "Reçu" };
const STATUS_BY_TYPE = {
  devis: [
    { key: "brouillon", label: "Brouillon" },
    { key: "envoye", label: "Envoyé" },
    { key: "accepte", label: "Accepté" },
    { key: "refuse", label: "Refusé" },
  ],
  facture: [
    { key: "brouillon", label: "Brouillon" },
    { key: "envoyee", label: "Envoyée" },
    { key: "payee", label: "Payée" },
    { key: "annulee", label: "Annulée" },
  ],
  recu: [{ key: "emis", label: "Émis" }],
};
const STATUS_COLOR = {
  brouillon: "border-muted-foreground/30 text-muted-foreground",
  envoye: "border-primary/40 text-primary",
  envoyee: "border-primary/40 text-primary",
  accepte: "border-[hsl(var(--success))]/40 text-[hsl(var(--success))]",
  payee: "border-[hsl(var(--success))]/40 text-[hsl(var(--success))]",
  emis: "border-[hsl(var(--success))]/40 text-[hsl(var(--success))]",
  refuse: "border-destructive/40 text-destructive",
  annulee: "border-destructive/40 text-destructive",
};

const emptyDoc = (type = "devis") => ({
  type,
  client_id: "",
  date: todayISO(),
  due_date: "",
  lines: [{ description: "", quantity: 1, unit_price: 0 }],
  discount: 0,
  tva_rate: 0,
  notes: "",
  payment_method: "",
  status: "",
  service_type: "transfert",
  reservation_date: "",
  reservation_time: "",
  pickup_date: "",
  pickup_time: "",
  pickup_address: "",
  dropoff_address: "",
});

export default function Documents() {
  const [tab, setTab] = useState("devis");
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyDoc("devis"));
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [d, c, s] = await Promise.all([
        api.get(`/documents?type=${tab}`),
        api.get("/clients"),
        api.get("/settings"),
      ]);
      setItems(d.data);
      setClients(c.data);
      setSettings(s.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const openCreate = () => {
    setForm({ ...emptyDoc(tab), client_id: clients[0]?.id || "" });
    setEditingId(null);
    setOpen(true);
  };

  const openEdit = async (id) => {
    const { data } = await api.get(`/documents/${id}`);
    setForm({
      type: data.type,
      client_id: data.client_id,
      date: data.date,
      due_date: data.due_date || "",
      lines: data.lines || [],
      discount: data.discount || 0,
      tva_rate: data.tva_rate || 0,
      notes: data.notes || "",
      payment_method: data.payment_method || "",
      status: data.status || "",
      service_type: data.service_type || "transfert",
      reservation_date: data.reservation_date || "",
      reservation_time: data.reservation_time || "",
      pickup_date: data.pickup_date || "",
      pickup_time: data.pickup_time || "",
      pickup_address: data.pickup_address || "",
      dropoff_address: data.dropoff_address || "",
    });
    setEditingId(id);
    setOpen(true);
  };

  const subtotal = useMemo(() => {
    return (form.lines || []).reduce((s, l) => s + (Number(l.quantity) || 0) * (Number(l.unit_price) || 0), 0);
  }, [form.lines]);
  const totalHT = Math.max(0, subtotal - (Number(form.discount) || 0));
  const tvaAmount = (totalHT * (Number(form.tva_rate) || 0)) / 100;
  const total = totalHT + tvaAmount;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.client_id) {
      toast.error("Sélectionnez un client");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        discount: Number(form.discount) || 0,
        tva_rate: Number(form.tva_rate) || 0,
        lines: form.lines.map((l) => ({
          description: l.description || "",
          quantity: Number(l.quantity) || 0,
          unit_price: Number(l.unit_price) || 0,
        })),
      };
      if (editingId) await api.put(`/documents/${editingId}`, payload);
      else await api.post("/documents", payload);
      toast.success(editingId ? "Document mis à jour" : "Document créé");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce document ?")) return;
    try {
      await api.delete(`/documents/${id}`);
      toast.success("Supprimé");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec de la suppression");
    }
  };

  const downloadPDF = async (doc) => {
    try {
      const { data } = await api.get(`/documents/${doc.id}`);
      generateDocumentPDF({ document: data, client: data.client, settings });
      toast.success("PDF téléchargé");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec de la génération du PDF");
    }
  };

  const convertTo = async (doc, target) => {
    if (!window.confirm(`Convertir en ${TYPE_LABEL[target].toLowerCase()} ?`)) return;
    try {
      const { data } = await api.post(`/documents/${doc.id}/convert?target_type=${target}`, {});
      toast.success(`${TYPE_LABEL[target]} créé${target === "facture" ? "e" : ""} : ${data.numero}`);
      setTab(target);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec de la conversion");
    }
  };

  const clientMap = Object.fromEntries(clients.map((c) => [c.id, `${c.prenom || ""} ${c.nom}`.trim()]));

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="overline mb-2">Facturation</div>
          <h1 className="font-manrope text-3xl md:text-4xl font-extrabold">Documents</h1>
          <p className="text-sm text-muted-foreground mt-1">Devis, factures et reçus</p>
        </div>
        <Button onClick={openCreate} disabled={clients.length === 0} data-testid="btn-add-document">
          <Plus className="h-4 w-4 mr-2" /> Nouveau {TYPE_LABEL[tab].toLowerCase()}
        </Button>
      </div>

      {clients.length === 0 && (
        <div className="rounded-md border border-primary/25 bg-primary/5 text-primary text-sm px-4 py-3">
          Ajoutez d&apos;abord un client dans l&apos;onglet <strong>Clients</strong> pour créer un document.
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="devis" data-testid="tab-devis">Devis</TabsTrigger>
          <TabsTrigger value="facture" data-testid="tab-facture">Factures</TabsTrigger>
          <TabsTrigger value="recu" data-testid="tab-recu">Reçus</TabsTrigger>
        </TabsList>
        <TabsContent value={tab} className="mt-4">
          <div className="card-surface overflow-hidden">
            {loading ? (
              <div className="p-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
            ) : items.length === 0 ? (
              <div className="border-dashed border-2 border-border/50 m-4 rounded-xl p-10 text-center">
                <FileText className="h-10 w-10 text-muted-foreground mx-auto mb-4" strokeWidth={1.5} />
                <div className="font-manrope font-semibold text-lg mb-1">Aucun {TYPE_LABEL[tab].toLowerCase()}</div>
                <div className="text-sm text-muted-foreground">Créez votre premier {TYPE_LABEL[tab].toLowerCase()}.</div>
              </div>
            ) : (
              <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[760px]">
                <thead className="text-xs text-muted-foreground border-b border-border">
                  <tr>
                    <th className="text-left px-4 py-3">N°</th>
                    <th className="text-left px-4 py-3">Date</th>
                    <th className="text-left px-4 py-3">Client</th>
                    <th className="text-left px-4 py-3">Statut</th>
                    <th className="text-right px-4 py-3">Total</th>
                    <th className="w-56"></th>
                  </tr>
                </thead>
                <tbody data-testid={`documents-tbody-${tab}`}>
                  {items.map((d) => (
                    <tr key={d.id} className="border-b border-border/60 hover:bg-muted/40 transition-colors">
                      <td className="px-4 py-3 font-medium num">{d.numero}</td>
                      <td className="px-4 py-3 num">{d.date}</td>
                      <td className="px-4 py-3">{clientMap[d.client_id] || d.client_name || "—"}</td>
                      <td className="px-4 py-3">
                        <Badge variant="outline" className={STATUS_COLOR[d.status] || ""}>{d.status}</Badge>
                      </td>
                      <td className="px-4 py-3 text-right num font-semibold">{fmtEuro(d.total)}</td>
                      <td className="px-2 py-3 text-right">
                        <div className="flex items-center justify-end gap-1 flex-wrap">
                          <button type="button" onClick={() => downloadPDF(d)} title="Télécharger PDF" className="text-muted-foreground hover:text-primary p-1.5 rounded-md hover:bg-primary/10 transition-colors" data-testid={`btn-pdf-${d.id}`}>
                            <Download className="h-4 w-4" />
                          </button>
                          <button type="button" onClick={() => openEdit(d.id)} title="Modifier" className="text-muted-foreground hover:text-foreground p-1.5 rounded-md hover:bg-muted transition-colors" data-testid={`btn-edit-doc-${d.id}`}>
                            <FileText className="h-4 w-4" />
                          </button>
                          {d.type === "devis" && (
                            <button
                              type="button"
                              onClick={() => convertTo(d, "facture")}
                              title="Convertir en facture"
                              className="ml-1 inline-flex items-center gap-1 text-xs font-semibold text-primary bg-primary/10 border border-primary/30 hover:bg-primary/20 px-2 py-1 rounded-md transition-colors"
                              data-testid={`btn-convert-${d.id}`}
                            >
                              <ArrowRightCircle className="h-3.5 w-3.5" /> Facturer
                            </button>
                          )}
                          {d.type === "facture" && d.status === "payee" && (
                            <button
                              type="button"
                              onClick={() => convertTo(d, "recu")}
                              title="Générer reçu"
                              className="ml-1 inline-flex items-center gap-1 text-xs font-semibold text-primary bg-primary/10 border border-primary/30 hover:bg-primary/20 px-2 py-1 rounded-md transition-colors"
                              data-testid={`btn-recu-${d.id}`}
                            >
                              <ArrowRightCircle className="h-3.5 w-3.5" /> Reçu
                            </button>
                          )}
                          <button type="button" onClick={() => remove(d.id)} title="Supprimer" className="text-muted-foreground hover:text-destructive p-1.5 rounded-md hover:bg-destructive/10 transition-colors" data-testid={`btn-delete-doc-${d.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Editor dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-manrope">
              {editingId ? "Modifier" : "Nouveau"} {TYPE_LABEL[form.type]?.toLowerCase()}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Field label="Client">
                <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                  <SelectTrigger data-testid="select-doc-client"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    {clients.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.prenom} {c.nom}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Date">
                <Input type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="input-doc-date" />
              </Field>
              <Field label={form.type === "devis" ? "Valable jusqu'au" : form.type === "facture" ? "Échéance" : "Date paiement"}>
                <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="input-doc-duedate" />
              </Field>
              <Field label="Statut">
                <Select value={form.status || STATUS_BY_TYPE[form.type][0].key} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="select-doc-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUS_BY_TYPE[form.type].map((s) => (
                      <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              {(form.type === "facture" || form.type === "recu") && (
                <Field label="Mode paiement">
                  <Input placeholder="Espèces, CB, virement…" value={form.payment_method} onChange={(e) => setForm({ ...form, payment_method: e.target.value })} data-testid="input-doc-payment" />
                </Field>
              )}
            </div>

            {/* VTC transport block */}
            <div className="rounded-lg border border-border p-3 space-y-3">
              <div className="flex items-center justify-between">
                <div className="overline">Détails prestation VTC</div>
                <Select value={form.service_type} onValueChange={(v) => setForm({ ...form, service_type: v })}>
                  <SelectTrigger className="max-w-[220px]" data-testid="select-doc-service-type"><SelectValue placeholder="Type de prestation" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="transfert">Transfert (A → B)</SelectItem>
                    <SelectItem value="mise_a_disposition">Mise à disposition</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Field label="Date de réservation"><Input type="date" value={form.reservation_date} onChange={(e) => setForm({ ...form, reservation_date: e.target.value })} data-testid="input-doc-reservation-date" /></Field>
                <Field label="Heure de réservation"><Input type="time" value={form.reservation_time} onChange={(e) => setForm({ ...form, reservation_time: e.target.value })} data-testid="input-doc-reservation-time" /></Field>
                <Field label="Date prise en charge"><Input type="date" value={form.pickup_date} onChange={(e) => setForm({ ...form, pickup_date: e.target.value })} data-testid="input-doc-pickup-date" /></Field>
                <Field label="Heure prise en charge"><Input type="time" value={form.pickup_time} onChange={(e) => setForm({ ...form, pickup_time: e.target.value })} data-testid="input-doc-pickup-time" /></Field>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Adresse prise en charge"><Input value={form.pickup_address} onChange={(e) => setForm({ ...form, pickup_address: e.target.value })} data-testid="input-doc-pickup-address" /></Field>
                <Field label="Adresse de dépose"><Input value={form.dropoff_address} onChange={(e) => setForm({ ...form, dropoff_address: e.target.value })} data-testid="input-doc-dropoff-address" /></Field>
              </div>
            </div>

            {/* Lines */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs">Lignes</Label>
                <button
                  type="button"
                  onClick={() => setForm({ ...form, lines: [...form.lines, { description: "", quantity: 1, unit_price: 0 }] })}
                  className="text-xs text-primary hover:underline flex items-center gap-1"
                  data-testid="btn-add-line"
                >
                  <Plus className="h-3 w-3" /> Ajouter une ligne
                </button>
              </div>
              <div className="space-y-2">
                {form.lines.map((l, i) => (
                  <div key={i} className="rounded-md border border-border/40 p-2 space-y-2 sm:space-y-0 sm:border-0 sm:p-0 sm:grid sm:grid-cols-12 sm:gap-2">
                    <Input className="sm:col-span-6" placeholder="Désignation" value={l.description} onChange={(e) => updateLine(form, setForm, i, "description", e.target.value)} data-testid={`input-line-desc-${i}`} />
                    <div className="grid grid-cols-[1fr_1fr_auto] gap-2 sm:contents">
                      <Input className="sm:col-span-2" type="number" step="0.1" inputMode="decimal" placeholder="Qté" value={l.quantity} onChange={(e) => updateLine(form, setForm, i, "quantity", e.target.value)} data-testid={`input-line-qty-${i}`} />
                      <Input className="sm:col-span-3" type="number" step="0.01" inputMode="decimal" placeholder="P.U. €" value={l.unit_price} onChange={(e) => updateLine(form, setForm, i, "unit_price", e.target.value)} data-testid={`input-line-price-${i}`} />
                      <button
                        type="button"
                        className="sm:col-span-1 text-muted-foreground hover:text-destructive"
                        onClick={() => setForm({ ...form, lines: form.lines.filter((_, idx) => idx !== i) })}
                        data-testid={`btn-remove-line-${i}`}
                      >
                        <Trash2 className="h-4 w-4 mx-auto" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Notes">
                <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="input-doc-notes" />
              </Field>
              <div className="space-y-2">
                <Row label="Sous-total HT" value={fmtEuro(subtotal)} />
                <Row label="Remise (€)">
                  <Input type="number" step="0.01" inputMode="decimal" value={form.discount} onChange={(e) => setForm({ ...form, discount: e.target.value })} className="max-w-[120px]" data-testid="input-doc-discount" />
                </Row>
                <Row label="Total HT" value={fmtEuro(totalHT)} />
                <Row label="% TVA">
                  <Input type="number" step="0.1" inputMode="decimal" value={form.tva_rate} onChange={(e) => setForm({ ...form, tva_rate: e.target.value })} className="max-w-[120px]" data-testid="input-doc-tva" />
                </Row>
                <Row label="Montant TVA" value={fmtEuro(tvaAmount)} />
                <div className="pt-2 border-t border-border">
                  <Row label="TOTAL TTC" bold value={fmtEuro(total)} />
                </div>
                {Number(form.tva_rate || 0) === 0 && (
                  <div className="text-[10px] text-muted-foreground italic">TVA non applicable, art. 293 B du CGI</div>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Annuler</Button>
              <Button type="submit" disabled={saving} data-testid="btn-save-document">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editingId ? "Enregistrer" : "Créer"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function updateLine(form, setForm, i, field, value) {
  const lines = [...form.lines];
  lines[i] = { ...lines[i], [field]: value };
  setForm({ ...form, lines });
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      {children}
    </div>
  );
}
function Row({ label, value, children, bold }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className={`text-muted-foreground ${bold ? "font-manrope font-bold text-foreground" : ""}`}>{label}</span>
      {children ? children : <span className={`num ${bold ? "font-manrope font-extrabold text-lg" : "font-semibold"}`}>{value}</span>}
    </div>
  );
}
